import type { LanPeer } from '../../lib/api';

export type FailedLanRooms = Map<string, number>;
export type LanElectionResult = {
  shouldCreate: boolean;
  winnerPeerId: string;
  candidates: string[];
  round: number;
  promoted: boolean;
};

export function pruneFailedLanRooms(failed: FailedLanRooms, now = Date.now()): void {
  for (const [roomId, retryAt] of failed.entries()) {
    if (retryAt <= now) failed.delete(roomId);
  }
}

export function markFailedLanRoom(
  failed: FailedLanRooms,
  roomId: string,
  retryMs: number,
  now = Date.now(),
): void {
  if (!roomId) return;
  failed.set(roomId, now + retryMs);
}

export function pickJoinableLanRoomId(
  peers: LanPeer[],
  failed: FailedLanRooms,
  now = Date.now(),
): string | null {
  let bestRoom: string | null = null;
  let bestSeen = -1;

  for (const p of peers) {
    const roomId = p.lanRoomId;
    if (!roomId) continue;
    const retryAt = failed.get(roomId);
    if (retryAt && retryAt > now) continue;
    if (p.lastSeen > bestSeen) {
      bestSeen = p.lastSeen;
      bestRoom = roomId;
    }
  }

  return bestRoom;
}

// Deterministic election across all visible peers.
// The lexicographically-smallest peerId creates the room.
export function electLanCreator(selfPeerId: string, peers: LanPeer[]): boolean {
  return resolveLanCreator(selfPeerId, peers).shouldCreate;
}

// Deterministic election with safe failover.
// Round 0 picks the smallest peerId. If no room appears for long enough,
// round 1 promotes the second-smallest, then round 2 the third, etc.
export function resolveLanCreator(
  selfPeerId: string,
  peers: LanPeer[],
  noRoomForMs = 0,
  failoverStepMs = 20_000,
): LanElectionResult {
  const candidateSet = new Set<string>([selfPeerId, ...peers.map((p) => p.peerId)]);
  const candidates = [...candidateSet].sort();
  const safeCandidates = candidates.length > 0 ? candidates : [selfPeerId];

  const safeStepMs = Math.max(1, failoverStepMs);
  const stableNoRoomMs = Math.max(0, noRoomForMs);
  const round = Math.floor(stableNoRoomMs / safeStepMs);
  const winnerIndex = Math.min(round, safeCandidates.length - 1);
  const winnerPeerId = safeCandidates[winnerIndex]!;

  return {
    shouldCreate: winnerPeerId === selfPeerId,
    winnerPeerId,
    candidates: safeCandidates,
    round,
    promoted: winnerIndex > 0,
  };
}
